/* ═══════════════════════════════════════════════════════════════
   Module Definitions — SpecForma feature licensing
   Defines purchasable modules and their nav/IPC mappings.
   ═══════════════════════════════════════════════════════════════ */

export type AppModule = 'core' | 'forma' | 'spec_manager' | 'project_hub' | 'time_billing';

export interface ModuleDef {
  name: string;
  description: string;
  navIds: string[];
}

/** Module definitions — what each module unlocks */
export const MODULE_DEFS: Record<AppModule, ModuleDef> = {
  core: {
    name: 'Core',
    description: 'Dashboard, settings, and basic administration',
    navIds: ['home', 'admin', 'settings'],
  },
  forma: {
    name: 'Forma',
    description: 'Word formatting templates, document formatter, template import/export',
    navIds: ['formas', 'format'],
  },
  spec_manager: {
    name: 'Spec Manager',
    description:
      'Spec master library, reusable module library, drafts, review/publish workflow, compile specs, AI import',
    navIds: ['spec-masters', 'smart-blocks', 'modules', 'compile', 'harvester'],
  },
  project_hub: {
    name: 'Project Hub',
    description:
      'Project management, client/organization management, team assignments, task management',
    navIds: ['projects', 'clients', 'team', 'tasks'],
  },
  time_billing: {
    name: 'Time & Billing',
    description: 'Timesheets, expenses, hour forecasting, invoicing, reports, bill rates',
    navIds: ['time-expense', 'invoices', 'reports'],
  },
};

/** Get all nav IDs that should be visible for a set of enabled modules */
export function getAllowedNavIds(enabledModules: AppModule[]): Set<string> {
  const allowed = new Set<string>();
  // Core is always enabled
  for (const id of MODULE_DEFS.core.navIds) allowed.add(id);
  for (const mod of enabledModules) {
    const def = MODULE_DEFS[mod];
    if (def) {
      for (const id of def.navIds) allowed.add(id);
    }
  }
  return allowed;
}

/** Check if a specific nav page is allowed */
export function isNavAllowed(navId: string, enabledModules: AppModule[]): boolean {
  return getAllowedNavIds(enabledModules).has(navId);
}

/**
 * IPC channel → required module mapping.
 * Channels not listed here are always allowed (core functionality).
 */
export const IPC_MODULE_MAP: Record<string, AppModule> = {
  // Forma module
  'standards:list': 'forma',
  'standards:save': 'forma',
  'standards:delete': 'forma',
  'engine:createTemplate': 'forma',
  'engine:importTemplate': 'forma',
  'engine:formatDocuments': 'forma',
  'tokens:load': 'forma',
  'tokens:save': 'forma',

  // Spec Manager module
  'blueprints:list': 'spec_manager',
  'blueprints:save': 'spec_manager',
  'blueprints:delete': 'spec_manager',
  'drafts:list': 'spec_manager',
  'drafts:save': 'spec_manager',
  'drafts:delete': 'spec_manager',
  'harvester:parseDocx': 'spec_manager',
  'harvester:extractDocx': 'spec_manager',
  'harvester:classifyAI': 'spec_manager',

  // Project Hub module
  'projects:list': 'project_hub',
  'projects:save': 'project_hub',
  'projects:delete': 'project_hub',
  'orgs:list': 'project_hub',
  'orgs:save': 'project_hub',
  'orgs:delete': 'project_hub',
  'team:list': 'project_hub',
  'team:save': 'project_hub',
  'team:delete': 'project_hub',
  'phases:list': 'project_hub',
  'phases:save': 'project_hub',
  'phases:delete': 'project_hub',
  'deliverables:list': 'project_hub',
  'deliverables:save': 'project_hub',
  'deliverables:delete': 'project_hub',
  'projectAssignments:list': 'project_hub',
  'projectAssignments:save': 'project_hub',
  'projectAssignments:delete': 'project_hub',
  'teams:list': 'project_hub',
  'teams:save': 'project_hub',
  'teams:delete': 'project_hub',
  'teamAssignments:list': 'project_hub',
  'teamAssignments:save': 'project_hub',
  'teamAssignments:delete': 'project_hub',
  'tasks:list': 'project_hub',
  'tasks:save': 'project_hub',
  'tasks:delete': 'project_hub',
  'taskAssignments:list': 'project_hub',
  'taskAssignments:save': 'project_hub',
  'taskAssignments:delete': 'project_hub',
  'taskComments:list': 'project_hub',
  'taskComments:save': 'project_hub',
  'taskComments:delete': 'project_hub',
  'taskNotifications:list': 'project_hub',
  'taskNotifications:save': 'project_hub',
  'taskNotifications:markRead': 'project_hub',
  'taskNotifications:delete': 'project_hub',

  // Time & Billing module
  'hours:list': 'time_billing',
  'hours:save': 'time_billing',
  'hours:delete': 'time_billing',
  'forecasts:list': 'time_billing',
  'forecasts:save': 'time_billing',
  'forecasts:delete': 'time_billing',
  'invoices:list': 'time_billing',
  'invoices:save': 'time_billing',
  'invoices:delete': 'time_billing',
  'timesheets:list': 'time_billing',
  'timesheets:getOrCreate': 'time_billing',
  'timesheets:updateStatus': 'time_billing',
  'timesheets:delete': 'time_billing',
  'timesheetAudit:list': 'time_billing',
  'expenses:list': 'time_billing',
  'expenses:save': 'time_billing',
  'expenses:delete': 'time_billing',
  'expenses:updateStatus': 'time_billing',
  'expenseAttachments:list': 'time_billing',
  'expenseAttachments:save': 'time_billing',
  'expenseAttachments:delete': 'time_billing',
  'billRates:list': 'time_billing',
  'billRates:save': 'time_billing',
  'billRates:delete': 'time_billing',
};

/** Get the required module for an IPC channel, or null if always allowed */
export function getRequiredModule(channel: string): AppModule | null {
  return IPC_MODULE_MAP[channel] || null;
}
